import axios from 'axios';
import { getApiBaseUrl } from './api';
import type { BillingCard } from './billingApi';

/* Project budgets: money on one builder project that pays for its builds and
 * QA. A provider funds a client's project from its own card; the client can
 * keep it going with its own card once that is used up. */

export interface ProjectBudget {
  project_id: string;
  title: string;
  status: string | null;
  created_at: string | null;
  has_budget: boolean;
  funded_cents: number;
  provider_cents: number;
  client_cents: number;
  spent_cents: number;
  provider_used_cents: number;
  client_used_cents: number;
  remaining_cents: number;
  paused: boolean;
  low: boolean;
  provider_left: boolean;
  provider_name: string;
  auto_topup: boolean;
  topup_threshold_cents: number | null;
  topup_amount_cents: number | null;
}

export interface ProjectBudgetList {
  available: boolean;
  retainer: boolean;
  projects: ProjectBudget[];
  card?: BillingCard | null;
}

export interface BudgetPerson {
  name: string | null;
  email: string | null;
  build_cents: number;
  qa_cents: number;
  total_cents: number;
}

export interface BudgetTicket {
  ticket_id: string;
  title: string;
  requested_by: { name: string | null; email: string | null } | null;
  build_cents: number;
  qa_cents: number;
  total_cents: number;
}

export interface BudgetPayment {
  payer: 'provider' | 'client';
  payer_name: string;
  kind: 'topup' | 'auto_topup';
  amount_cents: number;
  created_by: string | null;
  created_at: string | null;
}

export interface ProjectBudgetDetail extends ProjectBudget {
  tickets: BudgetTicket[];
  people: BudgetPerson[];
  unlinked_cents: number;
  box_cents: number;
  payments: BudgetPayment[];
  topup?: string;
}

export type BudgetSide = { kind: 'provider'; clientId: string } | { kind: 'client' };

const root = (side: BudgetSide) => {
  if (side.kind === 'provider') return `${getApiBaseUrl()}/provider/clients/${side.clientId}/project-budgets`;
  return `${getApiBaseUrl()}/billing/project-budgets`;
};

const confirmUrl = (side: BudgetSide) => {
  if (side.kind === 'provider') return `${getApiBaseUrl()}/provider/project-budgets/checkout/confirm`;
  return `${getApiBaseUrl()}/billing/project-budgets/checkout/confirm`;
};

export const projectBudgetsApi = {
  list: (side: BudgetSide) => axios.get<ProjectBudgetList>(root(side)),
  detail: (side: BudgetSide, projectId: string) => axios.get<ProjectBudgetDetail>(`${root(side)}/${projectId}`),
  startCheckout: (side: BudgetSide, projectId: string, amountCents: number, backUrl: string) =>
    axios.post<{ url: string; session_id: string }>(`${root(side)}/${projectId}/checkout`, {
      amount_cents: amountCents,
      success_url: backUrl,
      cancel_url: backUrl,
    }),
  confirmCheckout: (side: BudgetSide, sessionId: string) =>
    axios.post<{ paid_cents: number }>(confirmUrl(side), { session_id: sessionId }),
  setRetainer: (clientId: string, retainer: boolean) =>
    axios.patch<{ retainer: boolean }>(`${getApiBaseUrl()}/provider/clients/${clientId}/retainer`, { retainer }),
  setRule: (clientId: string, projectId: string, rule: { auto_topup: boolean; topup_threshold_cents: number; topup_amount_cents: number }) =>
    axios.patch<ProjectBudgetDetail>(`${getApiBaseUrl()}/provider/clients/${clientId}/project-budgets/${projectId}`, rule),
};
